import type { DesignerArrangement } from '@pz/scene';
import { el, textBtn, type DesignerCtx } from './panels.js';
import { openDialog } from './overlay.js';
import { canAddLayer, freshImageLayer, freshTextLayer } from './state.js';
import { rememberUpload } from './image-panel.js';
import { run } from './commands.js';
import { fillText } from './texts.js';

/**
 * The quick start: "Slika", "Tekst" or "Slika i tekst", and nothing else to
 * understand. A child taps one big button, picks a photo from the gallery,
 * types a name, and the design is already laid out on the product —
 * photo on top, name underneath, both as big as the zone allows. Everything
 * after that (templates, arrangements, colours) is optional polish.
 *
 * Each step is the ordinary engine underneath: the same upload, the same add
 * action, the same `arrange` command Studio and MCP use. Cancelling a step
 * leaves whatever was already added; "Vrati" takes any of it back.
 */

export type QuickKind = 'photo' | 'text' | 'photo-text';
export type Upload = { assetId: string; url: string; w: number; h: number };

const running = new WeakSet<DesignerCtx>();

/** Blocks the designer with a quiet "Učitavam sliku…" while an upload runs. */
function busyVeil(ctx: DesignerCtx, label: string): () => void {
  const veil = el('div', 'pzd-busy');
  veil.setAttribute('role', 'status');
  veil.append(el('span', 'pzd-spinner'), el('span', undefined, label));
  ctx.root.append(veil);
  return () => veil.remove();
}

/**
 * Opens the device's photo picker and uploads what comes back.
 * Resolves `undefined` when the shopper backs out or the zone is full.
 */
export async function pickPhoto(ctx: DesignerCtx): Promise<Upload | undefined> {
  const room = canAddLayer(ctx.store.state(), ctx.config, 'image');
  if (!room.ok) {
    ctx.toast(room.reason === 'max-images'
      ? fillText(ctx.texts.uploadTooMany, { n: room.limit ?? ctx.config.image.maxCount })
      : room.reason === 'kind' ? ctx.texts.toolNotAllowed : fillText(ctx.texts.maxLayers, { n: ctx.config.maxLayers }), 'error');
    return undefined;
  }
  const input = el('input', 'pzd-file');
  input.type = 'file';
  input.accept = 'image/*';
  input.setAttribute('aria-label', ctx.texts.upload);
  ctx.root.append(input);
  const file = await new Promise<File | undefined>((resolve) => {
    let settled = false;
    let focusTimer: number | undefined;
    const finish = (picked: File | undefined): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(focusTimer);
      window.removeEventListener('focus', onFocus);
      input.remove();
      resolve(picked);
    };
    // Older engines have no `cancel` event: the window gets focus back with no
    // file. The delay lets a slow `change` (a big HEIC being converted) land first.
    const onFocus = (): void => {
      window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => { if (!input.files?.length) finish(undefined); }, 1500);
    };
    input.addEventListener('change', () => finish(input.files?.[0]));
    input.addEventListener('cancel', () => finish(undefined));
    window.addEventListener('focus', onFocus);
    input.click();
  });
  if (!file) return undefined;
  const done = busyVeil(ctx, ctx.texts.pickingPhoto);
  try {
    const upload = await ctx.uploads.upload(file);
    rememberUpload(ctx, upload);
    return upload;
  } catch {
    ctx.toast(ctx.texts.uploadFailed, 'error');
    return undefined;
  } finally {
    done();
  }
}

/** One big field: "Upiši ime ili poruku". Resolves the words, or `undefined`. */
export function askText(ctx: DesignerCtx, initial = ''): Promise<string | undefined> {
  return new Promise((resolve) => {
    let answer: string | undefined;
    let input: HTMLTextAreaElement | undefined;
    const submit = (handle: { close(): void }): void => {
      const value = input?.value.trim() ?? '';
      answer = value || undefined;
      handle.close();
    };
    openDialog({
      ctx,
      title: ctx.texts.askTextTitle,
      className: 'pzd-ask',
      build: (body, handle) => {
        input = el('textarea', 'pzd-ask-input');
        input.rows = 2;
        input.value = initial;
        input.placeholder = ctx.texts.askTextPlaceholder;
        input.maxLength = ctx.config.text.maxChars;
        input.setAttribute('aria-label', ctx.texts.askTextTitle);
        input.setAttribute('autocapitalize', 'words');
        input.setAttribute('enterkeyhint', 'done');
        // Enter finishes; Shift+Enter is a second line for the grown-ups.
        input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' && !event.shiftKey && !event.isComposing) { event.preventDefault(); submit(handle); }
        });
        body.append(input);
        window.setTimeout(() => input?.focus(), 40);
      },
      footer: (row, handle) => row.append(
        textBtn(ctx.texts.askTextSkip, () => handle.close()),
        textBtn(ctx.texts.askTextOk, () => submit(handle), { className: 'pzd-primary' }),
      ),
      onClose: () => resolve(answer),
    });
  });
}

/**
 * Waits until the canvas has measured what was just added (a text box is
 * fitted to its ink after its first paint), so an arrangement works from real
 * sizes rather than estimates. Quiet for 300 ms, or 1.6 s at most.
 */
function settle(ctx: DesignerCtx): Promise<void> {
  return new Promise((resolve) => {
    const started = Date.now();
    let last = ctx.store.value();
    let quietSince = Date.now();
    const tick = (): void => {
      const now = ctx.store.value();
      if (now !== last) { last = now; quietSince = Date.now(); }
      if (Date.now() - quietSince >= 300 || Date.now() - started > 1600) { resolve(); return; }
      window.setTimeout(tick, 60);
    };
    window.setTimeout(tick, 60);
  });
}

/** The arrangement a fresh quick start ends with. */
export function quickArrangement(kind: QuickKind, hasPhoto: boolean, hasText: boolean): DesignerArrangement | undefined {
  if (hasPhoto && hasText) return 'photo-top';
  if (hasPhoto) return 'fill';
  if (hasText) return 'stack';
  return undefined;
}

export async function startFlow(ctx: DesignerCtx, kind: QuickKind): Promise<void> {
  if (running.has(ctx)) return;
  running.add(ctx);
  try {
    let photo = false, words = false;
    if (kind !== 'text' && ctx.config.allow.image) {
      const upload = await pickPhoto(ctx);
      if (!upload) return;
      ctx.dispatch({ type: 'add', layer: freshImageLayer(ctx.rawConfig, upload, ctx.addCount()), select: false });
      photo = true;
    }
    if (kind !== 'photo' && ctx.config.allow.text) {
      const text = await askText(ctx);
      if (text) {
        const room = canAddLayer(ctx.store.state(), ctx.config, 'text');
        if (room.ok) {
          const fontId = ctx.config.text.defaultFontId ?? ctx.config.text.fontIds[0] ?? ctx.fonts.list[0]?.id ?? '';
          await ctx.fonts.ensure(fontId).catch(() => undefined);
          ctx.dispatch({ type: 'add', layer: freshTextLayer(ctx.rawConfig, fontId, text, ctx.addCount()), select: false });
          words = true;
        }
      }
    }
    if (!photo && !words) return;
    await settle(ctx);
    const variant = quickArrangement(kind, photo, words);
    // Anything already in the zone joins the arrangement; if the mix does not
    // suit this variant, the shopper still has the strip to choose from.
    if (variant) run(ctx, { op: 'arrange', variant }, undefined, { quiet: true });
    ctx.dispatch({ type: 'select', id: null });
  } finally {
    running.delete(ctx);
  }
}
